import { Link } from 'react-router';
import { Image } from '@ui/common/components/Image';

export interface PageCardProps {
  /** The page's own name, e.g. "Player stats". */
  title: string;
  /** Breadcrumb trail to the page, e.g. "Stats › Players". Shown under the name. */
  path?: string;
  /** The tenant site logo — what marks the result as a page on this site rather than a record. */
  logo?: string;
  href: string;
  /** A nav page the editor set to open in a new tab. */
  sameTab?: boolean;
  onNavigate?: () => void;
}

/**
 * A navigation result: a link to a page within the site rather than to a record.
 *
 * Written for GlobalSearch's Pages section — a search for "stats" has to be able to return the
 * Stats page itself, not just the players on it. Deliberately the same shape as `ClubCard` and the
 * vertical `PlayerCard`: centred image, name beneath, one line of secondary text. In a mixed result
 * list a page should read as another card in the row, not as a different kind of object. The
 * chevron is what marks it as a jump rather than a record.
 */
export function PageCard({ title, path, logo, href, sameTab, onNavigate }: PageCardProps) {
  const body = (
    <>
      <span className="page-card__media">{logo && <Image className="page-card__logo" src={logo} alt="" />}</span>
      <h4 className="page-card__name">
        {title}
        <span className="page-card__chevron" aria-hidden="true">
          <svg width="8" height="12" viewBox="0 0 8 12" fill="none" focusable="false">
            <path
              d="M1.5 1l5 5-5 5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </h4>
      {/* Always rendered, even when empty: it reserves its line so cards with and without a
          breadcrumb keep the same height and a mixed row lines up along the bottom. */}
      <span className="page-card__path text-3xs">{path}</span>
    </>
  );

  return (
    <div className="page-card">
      {/* An editor-configured external page is a real link, not an in-app route. */}
      {sameTab === false ? (
        <a className="page-card__link" href={href} target="_blank" rel="noopener noreferrer" onClick={onNavigate}>
          {body}
        </a>
      ) : (
        <Link className="page-card__link" to={href} onClick={onNavigate}>
          {body}
        </Link>
      )}
    </div>
  );
}
